import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import FolderClient from '../client/folder/FolderClient';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import ComponentName from '../constant/ComponentName';
import { query } from '@trlogic/client-js';
import IEnrichmentVariableService from './IEnrichmentService';
import { QueryVariable } from '../domain/enrichment/EnrichmentDomain';
import EnrichmentClient from '../client/enrichment/EnrichmentClient';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import _ from 'lodash';
import Query = query.Query;
import { changeQueryDirty } from '../redux/actions/QueryActions';
import DeployErrorUtil from '../util/DeployErrorUtil';

class QueryVariableService implements IEnrichmentVariableService {
  private enrichmentClient: EnrichmentClient = GatewayContainer.getInstance().get(GatewayNames.ENRICHMENT_CLIENT);
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);

  public async save(_queryVariable: QueryVariable, isUpdate: boolean, folder: EnrichedComponentCategory): Promise<boolean> {
    const queryVariable = _.cloneDeep(_queryVariable);

    if (!this.isValid(queryVariable)) return Promise.resolve(false);

    const exist: { type: any; id: string } | undefined = await this.exist(queryVariable.name);
    if (exist && !isUpdate) {
      MessageUtil.error({ summary: `${exist.id} already used, ${exist.type}:${exist.id} is using` });
      return Promise.resolve(false);
    }

    try {
      const dto = { id: queryVariable.name, object: queryVariable };
      await this.enrichmentClient.save(dto);
      if (!isUpdate && folder != undefined) {
        await this.folderClient.addItemToCategory(ComponentName.ENRICHMENT, folder.id, queryVariable.name);
      }
      MessageUtil.success({ summary: 'Query variable saved' });
      dispatch(changeQueryDirty(false));
      return Promise.resolve(true);
    } catch (e) {
      console.log(e);
      return Promise.resolve(false);
    }
  }

  public async delete(id: string): Promise<boolean> {
    if (id != undefined && id.trim().length > 0) {
      try {
        const response: any = await this.enrichmentClient.remove(id);
        if (response.code) {
          return false;
        }
        MessageUtil.success({
          summary: 'Query Variable',
          detail: (
            <div>
              <b>{id}</b> {LanguageUtil.get({ id: 'messages.deleted', defaultMessage: `deleted` })}
            </div>
          ),
        });
        return true;
      } catch (e: any) {
        if (DeployErrorUtil.show(e, 'Query Variable')) {
          return false;
        }
        const dependencies: any = e.response.data;
        if (dependencies?.message == 'Dependency Exception') {
          dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.QUERY_VARIABLE));
        }
        MessageUtil.error({
          summary: 'Query Variable',
          detail: (
            <div>
              <b>{id}</b> {LanguageUtil.get({ id: 'messages.cannotRemoved', defaultMessage: 'can not deleted' })}
            </div>
          ),
        });
        return false;
      }
    } else {
      return false;
    }
  }

  public async deploy(queryVariable: QueryVariable): Promise<void> {
    if (queryVariable.name != undefined && queryVariable.name.trim().length > 0) {
      try {
        await this.enrichmentClient.deploy(queryVariable.name);
        MessageUtil.success({
          summary: 'Query Variable',
          detail: (
            <div>
              <b>{queryVariable.name}</b> {LanguageUtil.get({ id: 'messages.deploy', defaultMessage: `deployed` })}
            </div>
          ),
        });
      } catch (error: unknown) {
        if (DeployErrorUtil.show(error, 'Query Variable')) {
          return;
        }
        MessageUtil.error({
          summary: 'Query Variable',
          detail: (
            <div>
              <b>{queryVariable.name}</b> {LanguageUtil.get({ id: 'messages.cannotdeploy', defaultMessage: 'can not deploy' })}
            </div>
          ),
        });
        console.error(error);
      }
    } else {
      MessageUtil.error({
        summary: 'Query Variable',
        detail: (
          <div>
            <b>{queryVariable.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'Variable name cannot be empty' })}
          </div>
        ),
      });
    }
  }

  public async undeploy(id: string): Promise<void> {
    if (id != undefined) {
      try {
        await this.enrichmentClient.undeploy(id);
        MessageUtil.success({
          summary: 'Query Variable',
          detail: (
            <div>
              <b>{id}</b> {LanguageUtil.get({ id: 'messages.undeploy', defaultMessage: `undeploy` })}
            </div>
          ),
        });
      } catch (e) {
        if (DeployErrorUtil.show(e, 'Query Variable')) {
          return;
        }
        console.error(e);
      }
    } else {
      MessageUtil.error({
        summary: 'Query Variable',
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'Variable name cannot be empty' })}
          </div>
        ),
      });
    }
  }

  public async exist(id: string): Promise<{ type: any; id: string } | undefined> {
    if (id != undefined) {
      try {
        return this.enrichmentClient.exists(id);
      } catch (e) {
        console.error(e);
      }
    }
  }

  public async count(query: Query): Promise<number> {
    if (query != undefined) {
      try {
        return await this.enrichmentClient.count(query);
      } catch (e) {
        console.error(e);
      }
    }
    return 0;
  }

  public isValid(queryVariable: QueryVariable): boolean {
    if (queryVariable.name == undefined || queryVariable.name.trim().length == 0) {
      MessageUtil.warning({
        summary: 'Name can not be empty',
      });
      return false;
    } else if (queryVariable.timeout == undefined || String(queryVariable.timeout).trim().length == 0) {
      MessageUtil.warning({
        summary: 'Timeout can not be undefined',
      });
      return false;
    } else if (queryVariable.query == undefined || queryVariable.query.trim().length == 0) {
      MessageUtil.warning({
        summary: 'Query can not be undefined',
      });
      return false;
    } else if (queryVariable.transaction == undefined || queryVariable.transaction.trim().length == 0) {
      MessageUtil.warning({
        summary: 'Transaction can not be undefined',
      });
      return false;
    } else if (queryVariable.connection == undefined || queryVariable.connection.trim().length == 0) {
      MessageUtil.warning({
        summary: 'Connection can not be undefined',
      });
      return false;
    }
    return true;
  }
}

export default QueryVariableService;
